// Root app — wires React Router + AppShell. Per-page content lives in src/pages/.
//
// AuthGate wraps the route tree: while Firebase auth state is undetermined,
// shows a tiny pulsing "Checking sign-in…" panel; while no user is signed in,
// shows <LoginPage />; once signed in, mounts the real routes. The gate sits
// INSIDE BrowserRouter so LoginPage has access to react-router (in case it
// ever needs <Link> or useNavigate for password-reset flows, etc.) but OUTSIDE
// <Routes> so the route tree is never instantiated pre-auth.

import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import type { User } from 'firebase/auth';
import { observeAuthState } from './shared/firebase';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomersBulkTierPage } from './pages/CustomersBulkTierPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { QuotesPage } from './pages/QuotesPage';
import { QuoteDetailPage } from './pages/QuoteDetailPage';
import { PartsPage } from './pages/PartsPage';
import { PartDetailPage } from './pages/PartDetailPage';
import { PartWizardTestPage } from './pages/PartWizardTestPage';
import { OrdersPage } from './pages/OrdersPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { RfqPage } from './pages/RfqPage';
import { SettingsPage } from './pages/SettingsPage';
import { DevPage } from './pages/DevPage';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed_out' }
  | { kind: 'signed_in'; user: User };

function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });

  useEffect(() => {
    const unsub = observeAuthState((user) => {
      setState(user ? { kind: 'signed_in', user } : { kind: 'signed_out' });
    });
    return unsub;
  }, []);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen bg-paper text-text font-sans flex items-center justify-center">
        <div className="text-sm text-text-mute animate-pulse">Checking sign-in…</div>
      </div>
    );
  }
  if (state.kind === 'signed_out') {
    return <LoginPage />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthGate>
        <Routes>
          <Route element={<AppShell />}>
            <Route index element={<HomePage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/bulk-tier" element={<CustomersBulkTierPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="quotes" element={<QuotesPage />} />
            <Route path="quotes/:id" element={<QuoteDetailPage />} />
            <Route path="parts" element={<PartsPage />} />
            <Route path="parts/:id" element={<PartDetailPage />} />
            <Route path="parts/wizard-test" element={<PartWizardTestPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/:id" element={<OrderDetailPage />} />
            <Route path="rfq" element={<RfqPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="dev" element={<DevPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </AuthGate>
    </BrowserRouter>
  );
}

function NotFound() {
  return (
    <div className="max-w-2xl mx-auto text-center py-12">
      <div className="text-5xl mb-3">🤔</div>
      <h1 className="text-2xl font-bold text-navy mb-2">Page not found</h1>
      <p className="text-text-mute">
        That route doesn't exist yet. Use the nav up top.
      </p>
    </div>
  );
}
